import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaExternalLinkAlt, FaImage, FaInfoCircle, FaStore, FaFilter, FaHeart, FaStar, FaCheckCircle } from 'react-icons/fa';
import { businessNetworkApi } from '../../api/businessNetworkApi';
import { userApi } from '../../api/userApi';
import '../../css/business-listings.css';

export const BusinessListings = ({ network }) => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchBusinessListings = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use businessNetworkID from network object, default to 1
        const networkId = network?.businessNetworkID || 1;
        const response = await businessNetworkApi.fetchNetwork(networkId);

        if (response?.result && response.result.length > 0) {
          const networkData = response.result[0];
          const productListings = networkData.listings || [];
          setListings(productListings);

          // Extract unique categories
          const uniqueCategories = [...new Set(
            productListings.map(item =>
              item.productCategoryName || item.recordTypeName || 'Uncategorized'
            ).filter(Boolean)
          )];
          setCategories(uniqueCategories);

          // Fetch user data for all unique user IDs
          const userIds = [...new Set(productListings.map(p => p.walletUserRegistrationID).filter(Boolean))];
          const usersData = {};

          for (const userId of userIds) {
            try {
              const result = await userApi.findUserByIdFull(userId, networkId);
              if (result.user) {
                usersData[userId] = result.user;
              }
            } catch (err) {
              console.warn(`Failed to fetch user ${userId}:`, err);
            }
          }
          setUsers(usersData);
        }
      } catch (err) {
        console.error('Error fetching business listings:', err);
        setError(err.message || 'Failed to load business listings');
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessListings();
  }, [network]);

  const filteredListings = selectedCategory === 'all'
    ? listings
    : listings.filter(item => {
        const category = item.productCategoryName || item.recordTypeName || 'Uncategorized';
        return category === selectedCategory;
      });

  const handleViewDetails = (product) => {
    const networkId = network?.businessNetworkID || 1;
    navigate(`/product-detail/${networkId}/${product.productListItemID}`);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center business-listings-loading-container">
        <div className="text-center">
          <div className="spinner-border text-success mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading business listings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-warning business-listings-alert-warning" role="alert">
        <FaInfoCircle className="me-2" />
        <strong>Business Listings Unavailable:</strong> {error}
      </div>
    );
  }

  if (listings.length === 0) {
    return (
      <div className="alert alert-info business-listings-alert-info" role="alert">
        <FaInfoCircle className="me-2" />
        No business listings available for this network.
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Category Filter Badges */}
      <div className="mb-4">
        <div className="business-listings-filter-container">
          <h6 className="business-listings-filter-title">
            <FaFilter className="filter-icon" />
            Filter by Category
          </h6>
          <div className="d-flex flex-wrap gap-2">
            <button
              className={`btn ${selectedCategory === 'all' ? 'btn-primary' : 'btn-outline-primary'} business-listings-filter-button`}
              onClick={() => setSelectedCategory('all')}
            >
              All ({listings.length})
            </button>
            {categories.map(category => {
              const count = listings.filter(item => {
                const itemCategory = item.productCategoryName || item.recordTypeName || 'Uncategorized';
                return itemCategory === category;
              }).length;

              return (
                <button
                  key={category}
                  className={`btn ${selectedCategory === category ? 'btn-primary' : 'btn-outline-primary'} business-listings-filter-button`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category} ({count})
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Product Listings Grid */}
      <div className="row g-4">
        {filteredListings.map(product => {
          const owner = users[product.walletUserRegistrationID];
          const ownerIdentity = owner?.rootIdentityUrl || `User ID: ${product.walletUserRegistrationID}`;

          return (
            <div key={product.productListItemID} className="col-lg-6 col-xl-4">
              <div className="business-listings-product-card">

                {/* Card Header with Image Background */}
                <div
                  className="business-listings-card-header"
                  style={{
                    background: product.productImageUrl
                      ? `linear-gradient(135deg, rgba(99, 102, 241, 0.7), rgba(139, 92, 246, 0.8)), url(${product.productImageUrl})`
                      : undefined
                  }}
                >
                  {/* View Details Button - Moved to Top */}
                  <button
                    onClick={() => handleViewDetails(product)}
                    className="business-listings-view-details-button business-listings-view-details-top"
                  >
                    <FaExternalLinkAlt className="button-icon" />
                    Details
                  </button>

                  {/* Top Header - Logo and Badges */}
                  <div className="business-listings-header-top">
                  {/* Product Logo */}
                  <div className="business-listings-product-logo">
                    {product.productLogoUrl ? (
                      <img
                        src={product.productLogoUrl}
                        alt={`${product.productName} Logo`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="business-listings-product-logo-placeholder" style={{ display: product.productLogoUrl ? 'none' : 'flex' }}>
                      <FaImage />
                    </div>
                  </div>

                  {/* Status Badges */}
                  <div className="business-listings-status-badges">
                    {product.isVerified && (
                      <span className="business-listings-badge business-listings-badge-verified">
                        <FaCheckCircle className="badge-icon" />
                        Verified
                      </span>
                    )}
                    {product.isHot && (
                      <span className="business-listings-badge business-listings-badge-hot">
                        <FaStar className="badge-icon" />
                        Hot
                      </span>
                    )}
                    {product.isFavorite && (
                      <span className="business-listings-badge business-listings-badge-favorite">
                        <FaHeart className="badge-icon" />
                        Favorite
                      </span>
                    )}
                    {product.isSponsored && (
                      <span className="business-listings-badge business-listings-badge-sponsored">
                        Sponsored
                      </span>
                    )}
                  </div>
                </div>

                {/* Bottom Header - Title and Category */}
                <div>
                  <h5 className="business-listings-product-title">
                    {product.productName}
                  </h5>
                  <div className="business-listings-category-badge">
                    {product.productCategoryName || product.recordTypeName || 'Uncategorized'}
                  </div>
                </div>
              </div>

              {/* Card Body */}
              <div className="business-listings-card-body">

                {/* Product Code */}
                <div className="business-listings-product-code-section">
                  <small className="business-listings-product-code-label">Product Code</small>
                  <code className="business-listings-product-code">
                    {product.productCode}
                  </code>
                </div>

                {/* Description */}
                <div className="business-listings-description-section">
                  {product.productShortDescription && (
                    <>
                      <small className="business-listings-description-label">Description</small>
                      <p className="business-listings-description-text">
                        {product.productShortDescription}
                      </p>
                    </>
                  )}
                </div>

                {/* Footer with Technical Info */}
                <div className="business-listings-card-footer">
                  <small className="business-listings-footer-info">
                    Product ID: {product.productListItemID}
                  </small>
                  {network?.subDomain && (
                    <small className="business-listings-footer-info">
                      Network: {network.subDomain}
                    </small>
                  )}
                  <small className="business-listings-footer-info">
                    Owner: {ownerIdentity}
                  </small>
                </div>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {/* Results Count */}
      <div className="business-listings-results-count">
        Showing {filteredListings.length} of {listings.length} listings
        {selectedCategory !== 'all' && ` in "${selectedCategory}" category`}
      </div>
    </div>
  );
};